import type { GridColDef } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@vy/api-definition';
import { CUSTOM_FIELD_ENTITIES } from '@vy/custom-fields-types';
import { CUSTOMER_PREFIX } from '@vy/custom-fields-display';
import type { Account } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import { useMemo } from 'react';
import { getTableColumnsConfig } from '../../../../../hooks/use-table-columns-persistence';
import { useCustomFieldDefinitions } from '../../../../../hooks/requests/use-custom-field-definitions';
import { createCustomFieldColumns } from '../../../../../utils/create-custom-field-columns';
import { extractCustomFieldNamesFromLocalStorageConfig } from '@vy/custom-fields-display';
import { extractUniqueCustomFieldNames } from '../../../../../utils/extract-unique-custom-field-names';
import { getInvoicesOverviewTableColumns } from '../InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';
import { useInvoiceSyncIntegrationsEnabled } from '../../../../../hooks/requests/integrations';
import { useSubsidiaryNameMap } from '../../../../../hooks/requests/use-subsidiary-name-map';
import { useIsIssueOnEndOfMonthActive } from '../../../../../hooks/use-is-issue-on-end-of-month-active';

type UseInvoicesTableColumnsParams = {
  account: Account;
  tableName: string;
  items: InvoiceOverview[] | undefined;
  availableCustomFieldNames: string[] | undefined;
};

export function useInvoicesTableColumns({
  account,
  tableName,
  items,
  availableCustomFieldNames,
}: UseInvoicesTableColumnsParams): GridColDef<InvoiceOverview>[] {
  const { hasAnyInvoiceSyncIntegrationEnabled, isConnactEnabled } = useInvoiceSyncIntegrationsEnabled();
  const issueOnEndOfMonth = useIsIssueOnEndOfMonthActive();
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const { subsidiaryNameById, defaultSubsidiary } = useSubsidiaryNameMap();

  const definitionCustomNames = useMemo(() => {
    const allDefinitions = customFieldDefinitionsData?.customFieldDefinitions ?? [];
    const invoiceNames = allDefinitions
      .filter((def) => def.vayuEntity === CUSTOM_FIELD_ENTITIES.INVOICE)
      .map((def) => def.vayuCustomFieldName);
    const customerNames = allDefinitions
      .filter((def) => def.vayuEntity === CUSTOM_FIELD_ENTITIES.CUSTOMER)
      .map((def) => `${CUSTOMER_PREFIX}${def.vayuCustomFieldName}`);
    return [...invoiceNames, ...customerNames];
  }, [customFieldDefinitionsData]);

  return useMemo(() => {
    const baseColumns = getInvoicesOverviewTableColumns(account, {
      includeSyncColumn: hasAnyInvoiceSyncIntegrationEnabled,
      includeMarketplaceColumns: isConnactEnabled,
      issueOnEndOfMonth,
      subsidiaryNameById,
      defaultSubsidiary,
    });
    const baseFields = new Set(baseColumns.map((col) => col.field));

    const savedConfig = getTableColumnsConfig(tableName);
    const configCustomNames = extractCustomFieldNamesFromLocalStorageConfig(
      savedConfig?.visibility,
      baseFields,
    );

    const dataCustomNames = extractUniqueCustomFieldNames(
      (items ?? []).map((item) => item.customFieldValues),
    );

    const customFieldNames = [
      ...new Set([...configCustomNames, ...dataCustomNames, ...(availableCustomFieldNames ?? []), ...definitionCustomNames]),
    ];

    if (isEmptyArray(customFieldNames)) {
      return baseColumns;
    }

    const customFieldColumns = createCustomFieldColumns<InvoiceOverview>(customFieldNames);

    return [...baseColumns, ...customFieldColumns];
  }, [account, tableName, hasAnyInvoiceSyncIntegrationEnabled, isConnactEnabled, issueOnEndOfMonth, items, availableCustomFieldNames, definitionCustomNames, subsidiaryNameById, defaultSubsidiary]);
}
